import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { useQueryClient } from "@tanstack/react-query";

import { useDeleteRecording } from "../api/delete-recording";

type DeleteRecordingModalProps = {
  recordingId: string;
};

export function DeleteRecordingModal(props: DeleteRecordingModalProps) {
  const {
    mutateAsync: deleteRecording,
    isPending,
    isSuccess,
    isError,
    error,
    reset,
  } = useDeleteRecording();
  const [isDeleting, setIsDeleting] = useState(false);
  const { organisation } = useOrganisation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isDeleting) {
      reset();
    }
  }, [isDeleting, reset]);

  const handleDelete = async () => {
    try {
      await deleteRecording({
        id: props.recordingId,
        organisationId: organisation.id,
      });
      queryClient.invalidateQueries({ queryKey: ["recordings"] });
      setIsDeleting(false);
      navigate("/recordings");
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove recording</DialogTitle>
          <DialogDescription>
            Are you sure you would like to remove this recording?
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-foreground">
            Removing this recording will permanently delete it from the system.
          </p>
          <Alert variant="destructive" className="mt-4 flex items-center">
            <AlertCircle className="mt-1 h-4 w-4" />
            <AlertDescription className="pt-1">
              <p>This action is permanent and cannot be undone.</p>
            </AlertDescription>
          </Alert>
        </DialogBody>
        {isError && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isSuccess}
            isLoading={isPending || isSuccess}
            isAnimated
          >
            Remove Recording
          </Button>
        </DialogFooter>
      </DialogContent>
    </>
  );
}
